<template>
  <div class="gifts">
    <PageHeadlineWithLink
      title="使えるギフト"
      link-title="利用済・期限切れのギフト"
      @click-link="onClickHeadlineLink"
    />
    <div class="feedback">
      <transition name="fade" @enter="enterSuccessFeedback">
        <feedback-success
          v-if="showableSuccessFeedback"
          :message="successMessage"
        />
      </transition>
      <transition name="fade" @enter="enterErrorFeedback">
        <feedback-error v-if="showableErrorFeedback" :message="errorMessage" />
      </transition>
    </div>
    <div v-if="isLoadingTickets" class="gifts__loading">
      <loading />
    </div>
    <div v-else-if="!isEmptyResult" class="gift-contents">
      <div class="gift-contents__header-area">
        <div class="gift-contents__header">
          <sort
            :options="options"
            :selected-value="selectedSortKey"
            @change-sort="sortItems"
          />
          <div class="counter">
            <span class="denominator">
              {{ prevAndCurrentTicketsCount }}件/{{ totalGiftTicketsCount }}件
            </span>
          </div>
        </div>
      </div>
      <div class="gift-contents__body">
        <div class="gift-contents__gift-boxes">
          <gift-card-ticket-box
            v-for="giftTicket in giftTickets"
            :key="giftTicket.id"
            :sender-icon="giftTicket.senderIcon"
            :sender-name="giftTicket.senderName"
            :url="giftTicket.url"
            :item-name="giftTicket.itemName"
            :item-sku-name="giftTicket.itemSkuName"
            :item-sku-image="giftTicket.itemSkuImage"
            :brand-name="giftTicket.brandName"
            :ticket-id="giftTicket.id"
            :is-personal-use="giftTicket.isPersonalUse"
            :received-at="giftTicket.receivedAt"
            :usable-limit-at="giftTicket.usableLimitAt"
            :show-usable-profile-link="giftTicket.showUsableProfileLink"
            :created-user-profile-path="giftTicket.createdUserProfilePath"
            @click-button="showModal(giftTicket.id)"
          />
        </div>
        <div class="gift-contents__pagination">
          <Pagination
            :current-page="currentPage"
            :total-pages="totalPages"
            @clicked="changePage"
          />
        </div>
        <TextModal
          v-if="showableModal && ticketId !== null"
          :title-text="'利用済のギフトにする'"
          :caption-text="'利用済みに移動しますか？'"
          :primary-button-text="'はい'"
          :cancel-button-text="'閉じる'"
          @clicked-modal-cover="closeModal"
          @clicked-primary="clickedPrimary"
          @clicked-cancel="closeModal"
        />
      </div>
    </div>
    <div v-else class="gifts__empty-results">
      <p class="gifts__empty-result-message">
        保存したギフト、自分用に購入したギフトはここに表示されます
      </p>
    </div>
    <div class="gifts__to-giftee-top-area">
      <a href="/" class="gifts__to-giftee-top-link text-link-to-top">
        gifteeトップへ戻る
      </a>
    </div>
  </div>
</template>

<script>
import Pagination from '../../organism/paginations/Pagination.vue'
import Sort from '../../atoms/sorts/Sort.vue'
import GiftCardTicketBox from '../../organism/gift_card_tickets/GiftCardTicketBox.vue'
import Loading from '../../atoms/loadings/Loading.vue'
import FeedbackSuccess from '../../atoms/feedbacks/feedback-success.vue'
import FeedbackError from '../../atoms/feedbacks/feedback-error.vue'
import imageUrl from '../../../../../images/styleguide/none.png'
import TextModal from '../../molecules/modals/text-modal.vue'
import PageHeadlineWithLink from '../../molecules/headlines/PageHeadlineWithLink.vue'
import axios from 'axios'
import url from 'url'
import qs from 'qs'

const USABLE_GIFTS_SORT_KEY = 'usableGiftsSortPreference'

export default {
  components: {
    Pagination,
    Sort,
    GiftCardTicketBox,
    Loading,
    FeedbackSuccess,
    FeedbackError,
    TextModal,
    PageHeadlineWithLink
  },
  data() {
    return {
      giftTickets: [],
      selectedSortKey: 'received_at_desc',
      options: [
        { value: 'received_at_desc', key: '受け取り日の新しい順' },
        { value: 'received_at_asc', key: '受け取り日の古い順' },
        { value: 'usable_limit_at_asc', key: '有効期限が近い順' },
        { value: 'usable_limit_at_desc', key: '有効期限が遠い順' }
      ],
      isLoadingTickets: false,
      showPageCounts: [],
      displayItemCount: 10,
      showableSuccessFeedback: false,
      successMessage: '',
      showableErrorFeedback: false,
      errorMessage: '',
      showableModal: false,
      ticketId: null,
      currentPage: 1,
      totalPages: 1,
      totalGiftTicketsCount: 0
    }
  },
  computed: {
    isEmptyResult() {
      return this.giftTickets.length === 0
    },
    prevAndCurrentTicketsCount() {
      return (
        (this.currentPage - 1) * this.displayItemCount + this.giftTickets.length
      )
    }
  },
  async mounted() {
    this.initializeParams()
    // 保存された並び順の設定を取得
    const savedSortKey = this.getSavedSortPreference()
    if (savedSortKey) {
      this.selectedSortKey = savedSortKey
    }
    await this.getUsableGifts(this.currentPage)

    // NOTE: ブラウザバック・フォワード時の更新処理
    window.onpopstate = async function () {
      this.initializeParams()
      await this.getUsableGifts(this.currentPage)
    }.bind(this)
  },
  methods: {
    initializeParams() {
      const urlQuery = url.parse(location.href).query
      if (!urlQuery) {
        this.currentPage = 1
        return
      }
      const parsed = qs.parse(urlQuery)
      this.currentPage = parsed.page ? Number(parsed.page) : 1
    },
    updateUrlFromParams() {
      history.pushState(
        '',
        '',
        this.currentPage <= 1
          ? '/mypage/gifts'
          : `/mypage/gifts?page=${this.currentPage}`
      )
    },
    async getUsableGifts(page) {
      this.isLoadingTickets = true

      try {
        const res = await axios.get(
          `/api/v1/users/@me/gifts?usable=true&sort_key=${
            this.selectedSortKey
          }&page=${page || 1}&limit=${this.displayItemCount}`
        )
        if (res.data.data.length === 0) {
          this.giftTickets = []
          this.isLoadingTickets = false
          return
        }
        const { current_page, total_pages, total_count } = res.data.meta
        this.currentPage = current_page
        this.totalPages = total_pages
        this.totalGiftTicketsCount = total_count
        this.giftTickets = res.data.data.map(giftTicket => {
          const senderIcon =
            giftTicket.attributes.sender_icon !== null
              ? giftTicket.attributes.sender_icon
              : imageUrl

          // TODO: dummyimage を正式が画像に置換する
          const itemSkuImageUrl = giftTicket.attributes.item_sku_image
            ? giftTicket.attributes.item_sku_image
            : 'https://dummyimage.com/90x90/ccc/999.png&text=Not+Found'

          return {
            id: giftTicket.attributes.id,
            url: giftTicket.attributes.url,
            itemName: giftTicket.attributes.item_name,
            itemSkuImage: itemSkuImageUrl,
            itemSkuName: giftTicket.attributes.item_sku_name,
            brandName: giftTicket.attributes.brand_name,
            senderName: giftTicket.attributes.sender_name,
            senderIcon: senderIcon,
            isPersonalUse: giftTicket.attributes.personal_use,
            receivedAt: giftTicket.attributes.received_at,
            usableLimitAt: giftTicket.attributes.usable_limit_at,
            showUsableProfileLink:
              giftTicket.attributes.show_usable_profile_link,
            createdUserProfilePath:
              giftTicket.attributes.created_user_profile_path
          }
        })
        this.isLoadingTickets = false
      } catch (error) {
        this.isLoadingTickets = false
        this.showableErrorFeedback = true
        this.errorMessage = this.errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
      }
    },
    showModal(ticketId) {
      this.showableModal = true
      this.ticketId = ticketId
    },
    closeModal() {
      this.showableModal = false
      this.ticketId = null
    },
    async clickedPrimary() {
      try {
        const res = await axios.patch(
          `/api/v1/users/@me/gifts/${this.ticketId}`
        )
        if (res.data.status === 200) {
          await this.getUsableGifts(this.currentPage)

          this.successMessage = '利用済のギフトに移動しました'
          this.showableSuccessFeedback = true
          this.closeModal()
        }
      } catch (error) {
        this.showableErrorFeedback = true
        this.errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
      }
    },
    async changePage(page) {
      this.scrollToTop()
      await this.getUsableGifts(page)
      this.updateUrlFromParams()
    },
    scrollToTop() {
      window.scrollTo({ top, behavior: 'smooth' })
    },
    sortItems(sortKey) {
      this.selectedSortKey = sortKey
      this.saveSortPreference(sortKey)
      this.getUsableGifts()
    },
    enterSuccessFeedback() {
      setTimeout(() => {
        this.showableSuccessFeedback = false
      }, 3000)
    },
    enterErrorFeedback() {
      setTimeout(() => {
        this.showableErrorFeedback = false
      }, 3000)
    },
    onClickHeadlineLink() {
      location.href = '/mypage/used_gifts'
    },
    // 並び順の設定をlocalStorageに保存する
    saveSortPreference(sortKey) {
      localStorage.setItem(USABLE_GIFTS_SORT_KEY, sortKey)
    },
    // localStorageから並び順の設定を取得する
    getSavedSortPreference() {
      const savedPreference = localStorage.getItem(USABLE_GIFTS_SORT_KEY)
      // 保存された値が有効な並び順かチェック
      return this.options &&
        this.options.some(option => option.value === savedPreference)
        ? savedPreference
        : null
    }
  }
}
</script>
